import isURL from 'validator/es/lib/isURL';
import { get } from 'lodash';
import { checkRequired } from '@/utils/errors';
import {
  allOrSelected,
  findSelected,
  hasKey,
  hasMinimumLength,
  isPlainObject,
  removeEmpty,
  stringToCheck,
} from '@/utils/resultsHelper';
import fieldNames from '@/constants/fields';
import { prefixUrl, requestUrl, splitUrlArguments } from '@/constants/urls';
import { states } from '@/constants/lifecycle';
import sortMembersByEmail from '@/users/membership/helper';
import { datasetRequestStatus } from '@/datasets/status/helper';
import { controlsRequestsTypes } from '@/workflows/constants';
import { formFieldErrors } from '@/forms/helper';
import { transformForm } from '@/forms/state/state';
import nameSpaceErrors from '@/datasets/namespaces/helper';
import {
  requestTransferTypes,
  requestSources,
  approvalStatus,
  pollingStatus,
  requestSourceEntities,
  requestSteps,
} from './constants';
import { supportedRequestTransferTypes } from './state/state';

export const withDictionariesTransferType = (transferType) => ![requestTransferTypes.MANUAL, requestTransferTypes.TOKEN]
  .includes(transferType);

export const withSourceSelection = ({ allow_clear: allowClear, allow_pseudonymised: allowPmn }) => (
  allowClear || allowPmn
);

export const destinationInfoErrors = ({
  workspace,
  transferType,
  requestNewWorkspace,
  request: { destination_namespace: namespace, steps: { namespaceSelected, activeStepIndex } },
}) => {
  const title = `access.invalid.step.${Object.values(requestSteps)[activeStepIndex].name}`;
  const errors = [];

  checkRequired(transferType, [fieldNames.TYPE], errors, 'transfer', title);
  if (!requestNewWorkspace) {
    checkRequired(workspace?.uuid, [fieldNames.WORKSPACE], errors, 'workspace', title);
  }

  if (namespaceSelected) {
    errors.push(...nameSpaceErrors(namespace, 'workspace', title));
  }

  return errors;
};

export const cohortFromSelectedDictionaries = (name, { dictionaries }) => ({
  name,
  queries: [{
    name: 'Cohort query',
    clauses: allOrSelected(dictionaries).map((dictionary) => ({
      name: dictionary.name,
      dictionary: {
        code: dictionary.code,
      },
      filters: [],
    })),
  }],
});

export const cohortFromSelectedQueries = ({ code, queries }) => ({
  code,
  queries: findSelected(queries).map((query) => ({ id: query.id })),
});

export const requestSourcePartsFromUrl = (url) => {
  const [, type, requestSourceCode, action] = splitUrlArguments(url);
  const hasSourceType = action === requestSourceEntities.REQUEST
    && Object.values(requestSourceEntities).includes(type.slice(0, -1));

  return {
    hasSourceType,
    isNew: hasSourceType || type === requestSourceEntities.REQUEST,
    requestSourceCode,
    requestSourceType: hasSourceType ? type.slice(0, -1) : requestSources.DATASET,
  };
};

export const isNewRequest = (request, url) => {
  const isNewExists = hasKey(request, 'isNew');

  return ({
    isNew: isNewExists ? request.isNew : requestSourcePartsFromUrl(url).isNew,
    requestUrl: isNewExists && !request.isNew ? requestUrl(request) : url,
  });
};

export const requestSourceFromUrl = (url) => {
  const { requestSourceCode, hasSourceType, requestSourceType } = requestSourcePartsFromUrl(url);
  return { requestSourceCode: hasSourceType ? requestSourceCode : null, requestSourceType };
};

export const requestPollingStatus = ({
  saveStatus,
  waiting,
  steps,
  status,
}) => {
  let value = pollingStatus.NONE;

  if (saveStatus === states.ERROR) {
    value = pollingStatus.ERRORED;
  } else if (steps.pollStatus === states.SUCCESS && !waiting) {
    if (status === approvalStatus.PENDING) {
      value = pollingStatus.CREATED;
    } else if (status === approvalStatus.APPROVE) {
      value = pollingStatus.APPROVED;
    } else if (status === approvalStatus.ERROR) {
      value = pollingStatus.ERRORED;
    }
  } else if (steps.pollStatus === states.SUCCESS && status === approvalStatus.PENDING && waiting) {
    value = pollingStatus.DELAYED;
  } else if (saveStatus === states.SUCCESS && steps.pollStatus === states.PENDING) {
    value = pollingStatus.POLLING;
  }

  return value;
};

const requestStepNames = (steps = requestSteps) => Object.values(steps).map(({ name }) => name);

export const requestStepIndexOf = (name) => requestStepNames(requestSteps).indexOf(name);

export const requestStepIndexesOf = (names) => names.map(requestStepIndexOf);

const isTransferTypeAllowed = (request, allowPmnTransfer) => {
  const results = request?.steps?.results || [];
  if (results.length === 0) {
    return false;
  }

  const possibleTransferTypes = supportedRequestTransferTypes(results[0], allowPmnTransfer);

  if (!request.transfer.transfer_type && !!possibleTransferTypes.MANUAL) {
    return false;
  }
  if (request.transfer.transfer_type === requestTransferTypes.MANUAL) {
    return false;
  }

  return true;
};

const hasAnyAttachments = ({ attachments }) => attachments?.readStatus === states.SUCCESS
  && attachments?.results?.length > 0;

export const supportsAttachmentTransfers = ({ request, permissions, allowPmnTransfer }) => permissions.attachments.read
      && isTransferTypeAllowed(request, allowPmnTransfer);

export const supportsRequestWorkspace = ({ steps } = {}) => {
  const [source] = steps?.results ?? [];
  return source && source.status.request_workspace.supported;
};

export const allowedStepNames = (request, permissions, allowPmnTransfer) => {
  const steps = request.steps || {};
  if (Object.keys(steps).length === 0) { return []; }
  const names = requestStepNames();

  if (steps.sourceUrl != null) {
    names.splice(names.indexOf(requestSteps.SOURCE.name), 1);
  }

  if (!request.workspace_requested || !supportsRequestWorkspace(request, permissions)) {
    names.splice(names.indexOf(requestSteps.WORKSPACE.name), 1);
  }

  if (!supportsAttachmentTransfers({ request, permissions, allowPmnTransfer }) || !hasAnyAttachments(request.steps)) {
    names.splice(names.indexOf(requestSteps.ATTACHMENTS.name), 1);
  }

  return names;
};

export const compactRequestStepIndexes = (request, permissions, allowPmnTransfer) => {
  const indexes = requestStepIndexesOf(allowedStepNames(request, permissions, allowPmnTransfer));
  const currentIndex = indexes.indexOf(request.steps.activeStepIndex);
  const { length } = indexes;

  if (length <= 3) { return indexes; }
  if (currentIndex <= 0) { return [indexes[0], indexes[1], indexes[2]]; }
  if (currentIndex >= length - 1) { return [indexes[length - 3], indexes[length - 2], indexes[length - 1]]; }
  return [indexes[currentIndex - 1], indexes[currentIndex], indexes[currentIndex + 1]];
};

export const allowedRequestStepIndexes = (request, permissions, allowPmnTransfer) => (
  requestStepIndexesOf(allowedStepNames(request, permissions, allowPmnTransfer))
);

export const nextRequestStepIndex = ({ request, permissions, allowPmnTransfer }) => {
  const indexes = allowedRequestStepIndexes(request, permissions, allowPmnTransfer);
  const { activeStepIndex } = request.steps;

  return indexes[indexes.indexOf(activeStepIndex) + 1];
};

export const previousRequestStepIndex = ({ request, permissions, allowPmnTransfer }) => {
  const indexes = allowedRequestStepIndexes(request, permissions, allowPmnTransfer);
  const { activeStepIndex } = request.steps;

  return indexes[indexes.indexOf(activeStepIndex) - 1];
};

const userIsIncluded = (users, user) => removeEmpty(users).some((u) => u.uuid === user?.id);

const requestReadControl = (context, permissions) => ({
  allowAudit: (context.requester.isRequester || context.canApprove) && permissions.requests.audit,
  allowRead: permissions.requests.read,
});

const allowApproval = (controlsRequest) => controlsRequest === controlsRequestsTypes.FAIR;

const requestApproverControl = (context, permissions) => {
  const {
    request, requester, canApprove, canArchive,
  } = context;
  const { requests } = permissions;
  const controlsRequest = get(request, 'workflows[0].controls_requests', controlsRequestsTypes.FAIR);

  return {
    allowApprove: canApprove && requester.active && requests.review && allowApproval(controlsRequest),
    allowArchive: canArchive && !requester.isRequester && requests.destroy,
  };
};

const requestRequesterControl = (context, permissions) => {
  const {
    request, canApprove, requester, canDestroy,
  } = context;
  const { requests } = permissions;

  const dataset = get(request, 'datasets[0]');
  const allowClone = dataset ? datasetRequestStatus({ requests }, dataset).accessRequestSupported : false;

  return ({
    allowClone: requester.isRequester && allowClone && requests.create,
    allowDelete: requester.isRequester && canDestroy && requests.destroy,
    allowDownload: (requester.isRequester || canApprove) && requests.read,
  });
};

export const requestAccessContext = (request, user) => ({
  canApprove: !!request?.permissions?.can_approve,
  canArchive: !!request?.permissions?.can_archive,
  canDestroy: !!request?.permissions?.can_destroy,
  canTransfer: !!request?.permissions?.can_transfer,
  request,
  requester: {
    active: get(request, 'requester.active', false),
    isRequester: userIsIncluded([get(request, 'requester')], user),
  },
  waiting: get(request, 'waiting', true),
});

export const accessControlForTransfer = (context, { transfers }) => {
  const { canTransfer, request, waiting } = context;
  const readyForTransfer = get(request, 'transfer.ready_for_transfer', false);

  return ({
    allowTransfer: canTransfer && transfers.create && readyForTransfer && !waiting,
  });
};

export const accessControlAsApprover = (context, { permissions }) => ({
  ...requestReadControl(context, permissions),
  ...requestApproverControl(context, permissions),
  waiting: context.waiting,
});

export const accessControlAsRequester = (context, { permissions }) => ({
  ...requestReadControl(context, permissions),
  ...requestRequesterControl(context, permissions),
  ...accessControlForTransfer(context, permissions),
  waiting: context.waiting,
});

export const accessControlAsApproverOrRequester = (context, { permissions }) => ({
  ...requestApproverControl(context, permissions),
  ...accessControlAsRequester(context, { permissions }),
});

export const rightsUrl = ({ catalogue }) => {
  let rights = catalogue?.rights;

  if (Array.isArray(rights)) {
    [rights] = rights;
  }
  if (isPlainObject(rights)) {
    rights = rights.link;
  }

  const url = prefixUrl(stringToCheck(rights));
  return isURL(url) ? url : null;
};

export const sortedApproversByEmail = ({ datasets }) => {
  const owners = datasets ? datasets[0]?.approvers || [] : [];
  return [...owners].sort(sortMembersByEmail);
};

export const validateRequestSection = ({ fields }, { sections }, section) => (
  formFieldErrors(transformForm(fields), `access.invalid.step.${section}`, sections[section].fields, section)
);

export const requestTransferMessage = (supportsAutoTransfer, url) => {
  let message = supportsAutoTransfer ? 'request.mark.transfer.text' : 'request.manual.transfer.text';
  if (!hasMinimumLength(url)) { message = 'request.no.workspace.transfer.text'; }

  return message;
};

export default requestStepNames;
